import {
  useCallback,
  useRef,
  useState,
} from 'react'
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type PanInfo,
} from 'framer-motion'
import { ArrowRight, Check } from 'lucide-react'

import { cn } from '@/lib/utils'

interface SlideButtonProps {
  label?: string
  completedLabel?: string
  onComplete?: () => void
  disabled?: boolean
  className?: string
}

const DRAG_CONSTRAINTS = { left: 0, right: 220 }
const DRAG_THRESHOLD = 0.92

const ANIMATION_CONFIG = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 40,
  mass: 0.8,
}

export function SlideButton({
  className,
  label = 'Deslize para confirmar',
  completedLabel = 'Pronto!',
  onComplete,
  disabled = false,
}: SlideButtonProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [completed, setCompleted] = useState(false)
  const dragHandleRef = useRef<HTMLDivElement | null>(null)

  const dragX = useMotionValue(0)
  const springX = useSpring(dragX, ANIMATION_CONFIG)
  const dragProgress = useTransform(springX, [0, DRAG_CONSTRAINTS.right], [0, 1])
  const trackFill = useTransform(springX, (x) => x + 40)
  const labelOpacity = useTransform(springX, [0, DRAG_CONSTRAINTS.right * 0.6], [1, 0])

  const complete = useCallback(() => {
    if (completed || disabled) return

    setCompleted(true)
    dragX.set(DRAG_CONSTRAINTS.right)
    onComplete?.()
  }, [completed, disabled, dragX, onComplete])

  const handleDragStart = useCallback(() => {
    if (completed || disabled) return
    setIsDragging(true)
  }, [completed, disabled])

  const handleDragEnd = () => {
    if (completed || disabled) return
    setIsDragging(false)
    const progress = dragProgress.get()
    if (progress >= DRAG_THRESHOLD) {
      complete()
    } else {
      dragX.set(0)
    }
  }

  const handleDrag = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    if (completed || disabled) return
    const newX = Math.max(0, Math.min(info.offset.x, DRAG_CONSTRAINTS.right))
    dragX.set(newX)
  }

  return (
    <motion.div
      animate={{ width: completed ? '14rem' : '17rem' }}
      transition={ANIMATION_CONFIG}
      className={cn(
        'relative flex h-11 select-none items-center justify-center overflow-hidden rounded-full bg-muted',
        'shadow-[inset_0_1px_2px_0_rgba(15,23,42,0.06)]',
        disabled && 'opacity-60',
      )}
      aria-disabled={disabled}
    >
      {!completed && (
        <motion.div
          style={{ width: trackFill }}
          className="absolute inset-y-0 left-0 z-0 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500"
        />
      )}

      <motion.span
        style={{ opacity: completed ? 0 : labelOpacity }}
        className={cn(
          'pointer-events-none relative z-10 text-sm font-medium',
          isDragging || completed ? 'text-foreground/70' : 'text-muted-foreground',
        )}
      >
        {label}
      </motion.span>

      <AnimatePresence>
        {!completed && (
          <motion.div
            ref={dragHandleRef}
            drag={disabled ? false : 'x'}
            dragConstraints={DRAG_CONSTRAINTS}
            dragElastic={0.05}
            dragMomentum={false}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDrag={handleDrag}
            style={{ x: springX }}
            className="absolute left-0.5 top-1/2 z-20 -translate-y-1/2 cursor-grab active:cursor-grabbing"
          >
            <button
              type="button"
              disabled={disabled}
              onClick={complete}
              aria-label={label}
              className={cn(
                'flex aspect-square size-10 items-center justify-center rounded-full bg-foreground text-background shadow-md outline-none transition-transform',
                isDragging && 'scale-105',
                'disabled:cursor-not-allowed disabled:opacity-60',
                className,
              )}
            >
              <ArrowRight className="size-5" strokeWidth={2.5} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {completed && (
          <motion.div
            className="absolute inset-0 z-20 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 text-sm font-semibold text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Check className="h-4 w-4" strokeWidth={3} />
            {completedLabel}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
